import { useCallback, useState, type ReactNode, type Ref } from "react";
import { Group, Stack } from "@mantine/core";
import type { Cover } from "@/core/cover";
import { coverSrc, coverSrcSet } from "@/core/cover";
import { gradientFor } from "@/core/gradient";
import styles from "./styles/Curated.module.css";

interface CoverPictureProps {
    cover: Cover;
    className?: string;
    /** Widths offered to the browser, in pixels */
    widths?: number[];
    sizes: string;
    /** The photo at the top of the page: load it at once */
    eager?: boolean;
}

/** The picture, hotlinked from its source. It is decoration: the title and text carry the meaning. */
export const CoverPicture = ({
    cover,
    className,
    widths = [480, 800, 1080],
    sizes,
    eager = false,
}: CoverPictureProps) => (
    <img
        alt=""
        className={className}
        decoding="async"
        fetchPriority={eager ? "high" : undefined}
        loading={eager ? "eager" : "lazy"}
        sizes={sizes}
        src={coverSrc(cover.src, widths[Math.min(1, widths.length - 1)])}
        srcSet={coverSrcSet(cover.src, widths) || undefined}
    />
);

/**
 * What a tile shows where its picture would be when it has none: a gradient that is always the same for the same
 * seed (the slug). It is decoration like the picture, and needs no credit.
 */
export const GradientCover = ({ seed, className }: { seed: string; className?: string }) => (
    <div aria-hidden className={className} style={{ background: gradientFor(seed) }} />
);

/** Room above the title of a tile for a credit that fits on one line: the same for every tile, so titles line up */
const CREDIT_ROOM = 40;
/** Space between the bottom of a credit that wraps and the title below it */
const CREDIT_GAP = 16;

/**
 * How much room a tile's title needs above it for its credit. The credit sits in the corner of the picture, outside
 * the tile's link, and can take one line or two depending on the width of the tile and the length of the author's
 * name, so it is measured: a credit on one line leaves the title where it is on every other tile, and one that wraps
 * pushes the title down just enough. Attach the returned ref to the credit.
 */
export const useCreditRoom = (): [room: number, ref: Ref<HTMLParagraphElement>] => {
    const [room, setRoom] = useState(CREDIT_ROOM);
    const ref = useCallback((el: HTMLParagraphElement | null) => {
        if (!el || typeof ResizeObserver === "undefined") return;
        const measure = () => setRoom(Math.max(CREDIT_ROOM, Math.ceil(el.offsetHeight) + CREDIT_GAP));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return [room, ref];
};

/**
 * What darkens the picture behind the text. A photo can be any colour, so it gets a strong scrim; a gradient is
 * made of colours chosen to carry white text, so it only gets a faint one and keeps its colour.
 */
const SCRIMS = {
    photo: "linear-gradient(to bottom, rgb(0 0 0 / 75%) 0%, rgb(0 0 0 / 0%) 50%, rgb(0 0 0 / 0%) 55%, rgb(0 0 0 / 75%) 100%)",
    light: "linear-gradient(to bottom, rgb(0 0 0 / 20%) 0%, rgb(0 0 0 / 0%) 45%, rgb(0 0 0 / 0%) 55%, rgb(0 0 0 / 22%) 100%)",
} as const;

/** A tight edge and a wide halo, so the text reads on light photos as well as dark ones. Inherited by the text. */
const TEXT_SHADOW = "0 1px 2px rgb(0 0 0 / 85%), 0 2px 14px rgb(0 0 0 / 75%)";

/**
 * The text of a tile laid over its photo on a dark gradient, so white text reads on any photo: the title top left
 * (under the credit), the count bottom left and, if there is one, something at the bottom right. Put it inside the box that holds the
 * photo.
 */
export const CoverOverlay = ({
    title,
    count,
    aside,
    creditRoom = CREDIT_ROOM,
    scrim = "photo",
}: {
    title: ReactNode;
    count?: ReactNode;
    aside?: ReactNode;
    creditRoom?: number;
    scrim?: keyof typeof SCRIMS;
}) => (
    <Stack
        bg={SCRIMS[scrim]}
        data-scrim={scrim}
        gap={4}
        inset={0}
        justify="space-between"
        p="sm"
        data-credit-room={creditRoom}
        pt={creditRoom}
        pos="absolute"
        style={{ textShadow: TEXT_SHADOW }}>
        {title}
        <Group align="flex-end" gap="xs" justify="space-between" wrap="nowrap">
            <div>{count}</div>
            {aside}
        </Group>
    </Stack>
);

/**
 * The credit that goes with a cover, as links. Unsplash: the photographer and Unsplash. Wikimedia Commons: the
 * author, the license (linked when it has a page) and Commons; a public domain picture whose author is not known
 * has no author to name.
 */
export const CoverCredit = ({
    cover,
    className = "",
    ref,
}: {
    cover: Cover;
    className?: string;
    ref?: Ref<HTMLParagraphElement>;
}) => {
    if (cover.source === "commons") {
        return (
            <p className={`${styles.credit} ${className}`} ref={ref}>
                Bild:{" "}
                {cover.creditName && (
                    <>
                        {/* an author with no page of their own is linked to the file page, which "Wikimedia Commons" links to already */}
                        {cover.creditUrl === cover.sourceUrl ? (
                            cover.creditName
                        ) : (
                            <a href={cover.creditUrl} rel="noopener noreferrer" target="_blank">
                                {cover.creditName}
                            </a>
                        )}
                        ,{" "}
                    </>
                )}
                {cover.licenseUrl ? (
                    <a href={cover.licenseUrl} rel="noopener noreferrer" target="_blank">
                        {cover.licenseName}
                    </a>
                ) : (
                    cover.licenseName
                )}{" "}
                på{" "}
                <a href={cover.sourceUrl} rel="noopener noreferrer" target="_blank">
                    Wikimedia Commons
                </a>
            </p>
        );
    }
    return (
        <p className={`${styles.credit} ${className}`} ref={ref}>
            Foto:{" "}
            <a href={cover.creditUrl} rel="noopener noreferrer" target="_blank">
                {cover.creditName}
            </a>{" "}
            på{" "}
            <a href={cover.sourceUrl} rel="noopener noreferrer" target="_blank">
                Unsplash
            </a>
        </p>
    );
};
